import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import CssBaseline from '@material-ui/core/CssBaseline';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import CustomizedSnackbars from '../_commons/CustomizedSnackbars.jsx';
import FullScreenLoadingProgress from '../_commons/FullScreenLoadingProgressComponent/FullScreenLoadingProgress';
import Copyright from '../SignInPage/Copyright';
import { PasswordResetService } from '../../_services/PasswordResetService';
import { defaultPasswordValidator } from '../../_commons/Utils/PasswordValidator';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
    position: 'relative'
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

const ErrorMessagesText = {
  INVALID_TOKEN: "Invalid or expired password reset link. Please request a new password reset email.",
  SERVER_ERROR: "An error occurred while setting your password. Please try again.",
};

const SuccessMessagesText = {
  PASSWORD_SET_SUCCESS: "Password set successfully! Redirecting to sign in...",
};

export default function SetPassword() {
  const classes = useStyles();
  let history = useHistory();
  const location = useLocation();

  const [Password, setPassword] = useState("");
  const [ConfirmPassword, setConfirmPassword] = useState("");
  const [WaitingResult, setWaitingResult] = useState(false);
  const [ErrorMessages, setErrorMessages] = useState<string[]>([]);
  const [SuccessMessages, setSuccessMessages] = useState<string[]>([]);
  const [Token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // Extract token from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const tokenParam = searchParams.get('token');

    if (!tokenParam) {
      SetPasswordFailedHandler(ErrorMessagesText.INVALID_TOKEN);
    } else {
      setToken(tokenParam);
    }

    if (passwordInputRef && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [location]);

  /**
   * Operation on Password Input TextField
   */
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const PasswordFocusHandler = () => {
    if (passwordInputRef && passwordInputRef.current)
      passwordInputRef.current.setSelectionRange(0, passwordInputRef.current.value.length);
  };
  const PasswordChangeHandler = (event: any) => {
    setPassword(event.target.value);
  };
  const PasswordKeyDownHandler = (event: any) => {
    if (event.key === 'Enter') {
      if (confirmPasswordInputRef && confirmPasswordInputRef.current)
        confirmPasswordInputRef.current.focus();
    }
  };

  /**
   * Operation on Confirm Password Input TextField
   */
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);
  const ConfirmPasswordFocusHandler = () => {
    if (confirmPasswordInputRef && confirmPasswordInputRef.current)
      confirmPasswordInputRef.current.setSelectionRange(0, confirmPasswordInputRef.current.value.length);
  };
  const ConfirmPasswordChangeHandler = (event: any) => {
    setConfirmPassword(event.target.value);
  };
  const ConfirmPasswordKeyDownHandler = (event: any) => {
    if (event.key === 'Enter') {
      SetPasswordSubmitHandler(event);
    }
  };

  /**
   * Operation on Set Password Submit
   */
  const SetPasswordSubmitHandler = (e: any) => {
    e.preventDefault();

    if (!Token) {
      SetPasswordFailedHandler(ErrorMessagesText.INVALID_TOKEN);
      return;
    }

    // Validate password using the password validator
    const validationResult = defaultPasswordValidator.validatePasswordWithConfirmation(Password, ConfirmPassword);

    if (!validationResult.isValid) {
      validationResult.errors.forEach(error => {
        SetPasswordFailedHandler(error);
      });
      return;
    }

    setWaitingResult(true);
    PasswordResetService.SetPassword(Token, Password)
      .then(async (resp) => {
        if (resp.status === 200) {
          SetPasswordSuccessHandler();
        } else {
          const errorText = await resp.text();
          SetPasswordFailedHandler(errorText || ErrorMessagesText.SERVER_ERROR);
        }
      })
      .catch((error) => {
        SetPasswordFailedHandler(ErrorMessagesText.SERVER_ERROR);
      });
  };

  const SetPasswordSuccessHandler = () => {
    setSuccessMessages(SuccessMessages => [...SuccessMessages, SuccessMessagesText.PASSWORD_SET_SUCCESS]);
    setWaitingResult(false);

    // Redirect to sign in page after 3 seconds
    setTimeout(() => {
      history.push('/SignIn');
    }, 3000);
  };

  const SetPasswordFailedHandler = (error: string) => {
    setErrorMessages(ErrorMessages => [...ErrorMessages, error]);
    setWaitingResult(false);
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar} title={`RADACAD Version${process.env.REACT_APP_VERSION_NUMBER ?? "??"}`}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Set Your Password
        </Typography>

        <form className={classes.form} noValidate>
          {WaitingResult ? (
            <FullScreenLoadingProgress style={{ position: 'absolute', height: '100%', width: '100%', zIndex: 999 }}></FullScreenLoadingProgress>
          ) : (
            <></>
          )}
          <div style={WaitingResult ? { opacity: '0.1' } : {}}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="New Password"
              type="password"
              id="password"
              autoComplete="new-password"
              inputRef={passwordInputRef}
              onFocus={PasswordFocusHandler}
              onChange={PasswordChangeHandler}
              onKeyDown={PasswordKeyDownHandler}
              className={"myThemeBorder"}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              inputRef={confirmPasswordInputRef}
              onFocus={ConfirmPasswordFocusHandler}
              onChange={ConfirmPasswordChangeHandler}
              onKeyDown={ConfirmPasswordKeyDownHandler}
              className={"myThemeBorder"}
            />

            <Button
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              onClick={SetPasswordSubmitHandler}
            >
              Set Password
            </Button>
          </div>
        </form>
      </div>

      {ErrorMessages !== null &&
        ErrorMessages.map((errorMessage, index) => (
          <CustomizedSnackbars key={index} message={errorMessage} severity="error"></CustomizedSnackbars>
        ))}

      {SuccessMessages !== null &&
        SuccessMessages.map((successMessage, index) => (
          <CustomizedSnackbars key={index} message={successMessage} severity="success"></CustomizedSnackbars>
        ))}

      <Box mt={8}>
        <Copyright />
      </Box>
    </Container>
  );
}
